/**
 * Água andando nos trechos, pelo resultado da simulação — Nexora Grid (S3 v2).
 *
 * Criado em: 16/10/2026 · Última modificação: 16/10/2026 · Versão: 1.0.0
 *
 * Um ponto de luz a cada ~12 m de tubo, todos num único `THREE.Points` (uma chamada de desenho). O deslocamento
 * acumulado por trecho (fase += ciclos × Δt, sem saltos quando a velocidade muda) e aplicado no shader: cada ponto anda do início ao fim do trecho no sentido da vazão, com velocidade proporcional
 * à velocidade da água (exagerada para ser visível na escala da cidade). Trecho parado (|v| < 0,01 m/s) apaga.
 * As velocidades são interpoladas entre as horas da simulação.
 */
import { useFrame } from "@react-three/fiber";
import { useMemo, useRef } from "react";
import * as THREE from "three";
import type { Rede } from "./rede";
import type { Simulacao } from "./simulacao";
import { segmentosTubulacao } from "./malhaRede";

const ESPACAMENTO_M = 12;
const EXAGERO_VELOCIDADE = 45; // 1 m/s real ≈ 45 m/s na tela
const MAX_PONTOS = 40000;

export type Tempo = { hora: number; tocando: boolean; ritmo: number };

const vertice = /* glsl */ `
  attribute vec3 aInicio;
  attribute vec3 aFim;
  attribute float aFase;
  attribute float aDesloc;  // fase acumulada do trecho (ciclos)
  attribute float aAtivo;
  attribute vec3 aCor;
  attribute float aRaio;
  attribute float aRaioReal;
  uniform float uTamanho;
  uniform float uTubulacao;
  varying vec3 vCor;
  varying float vAtivo;
  void main() {
    float u = fract(aFase + aDesloc);
    vec3 p = mix(aInicio, aFim, u);
    float raio = mix(aRaioReal, aRaio, smoothstep(80.0, 1200.0, distance(cameraPosition, p)));
    p.y += uTubulacao > 0.5 ? raio + 0.12 : 0.4;
    vec4 mv = modelViewMatrix * vec4(p, 1.0);
    gl_Position = projectionMatrix * mv;
    vAtivo = aAtivo;
    gl_PointSize = vAtivo * clamp(uTamanho * 260.0 / -mv.z, 2.5, 22.0);
    vCor = aCor;
  }
`;

const fragmento = /* glsl */ `
  varying vec3 vCor;
  varying float vAtivo;
  void main() {
    if (vAtivo < 0.5) discard;
    float d = length(gl_PointCoord - 0.5);
    if (d > 0.5) discard;
    gl_FragColor = vec4(vCor, smoothstep(0.5, 0.15, d));
  }
`;

export function Agua({ rede, sim, tempo, altura, corDoTrecho, tubulacao = true }: {
  rede: Rede; sim: Simulacao; tempo: React.MutableRefObject<Tempo>;
  altura: (x: number, z: number) => number; corDoTrecho: (setor: string) => string;
  tubulacao?: boolean;
}) {
  const relogio = useRef(1);
  const { geometria, trechoDoPonto, comprimentos, ids } = useMemo(() => {
    const inicio: number[] = [], fim: number[] = [], fase: number[] = [], cor: number[] = [], raios: number[] = [], reais: number[] = [];
    const trechoDoPonto: number[] = [];
    const comprimentos: number[] = [];
    const ids: string[] = [];
    const c = new THREE.Color();
    segmentosTubulacao(rede, altura).forEach(({ de: a, para: b, raio, trecho: t }) => {
      if (inicio.length / 3 >= MAX_PONTOS) return;
      const k = ids.length;
      ids.push(t.id);
      const comprimento = Math.max(1, a.distanceTo(b));
      comprimentos.push(comprimento);
      const n = Math.min(MAX_PONTOS - inicio.length / 3, Math.max(1, Math.round(comprimento / ESPACAMENTO_M)));
      c.set(corDoTrecho(t.setor)).lerp(new THREE.Color("#ffffff"), 0.6);
      for (let i = 0; i < n; i++) {
        // Marcadores na parte superior dos tubos sólidos, acompanhando o mesmo perfil do terreno.
        inicio.push(a.x, a.y, a.z);
        fim.push(b.x, b.y, b.z);
        raios.push(raio);
        reais.push(THREE.MathUtils.clamp((Number.isFinite(t.dn) && t.dn > 0 ? t.dn : 100) / 2000, 0.01, 1.5));
        fase.push(i / n);
        cor.push(c.r, c.g, c.b);
        trechoDoPonto.push(k);
      }
    });
    const g = new THREE.BufferGeometry();
    g.setAttribute("aInicio", new THREE.Float32BufferAttribute(inicio, 3));
    g.setAttribute("aFim", new THREE.Float32BufferAttribute(fim, 3));
    g.setAttribute("aFase", new THREE.Float32BufferAttribute(fase, 1));
    g.setAttribute("aDesloc", new THREE.Float32BufferAttribute(new Float32Array(fase.length), 1));
    g.setAttribute("aAtivo", new THREE.Float32BufferAttribute(new Float32Array(fase.length), 1));
    g.setAttribute("aCor", new THREE.Float32BufferAttribute(cor, 3));
    g.setAttribute("aRaio", new THREE.Float32BufferAttribute(raios, 1));
    g.setAttribute("aRaioReal", new THREE.Float32BufferAttribute(reais, 1));
    g.setAttribute("position", new THREE.Float32BufferAttribute(inicio, 3)); // só para o recorte da câmera
    g.computeBoundingSphere();
    return { geometria: g, trechoDoPonto, comprimentos, ids };
  }, [rede, altura, corDoTrecho, tubulacao]);

  const material = useMemo(() => new THREE.ShaderMaterial({
    vertexShader: vertice, fragmentShader: fragmento, transparent: true, depthWrite: false,
    blending: THREE.AdditiveBlending, uniforms: { uTamanho: { value: 4.5 }, uTubulacao: { value: 1 } },
  }), []);

  const ciclos = useMemo(() => new Float32Array(ids.length), [ids]);
  const fases = useMemo(() => new Float32Array(ids.length), [ids]);
  useFrame((_, delta) => {
    material.uniforms.uTubulacao.value = tubulacao ? 1 : 0;
    const passo = Math.min(delta, 0.1);
    for (let k = 0; k < fases.length; k++) fases[k] = (fases[k] + ciclos[k] * passo) % 1;
    const desloc = geometria.getAttribute("aDesloc") as THREE.BufferAttribute;
    const d = desloc.array as Float32Array;
    for (let i = 0; i < d.length; i++) d[i] = fases[trechoDoPonto[i]];
    desloc.needsUpdate = true;
    relogio.current += delta;
    if (relogio.current < 0.25) return; // velocidades interpoladas 4× por segundo
    relogio.current = 0;
    const h = tempo.current.hora;
    const h0 = Math.floor(h) % sim.horas;
    const h1 = (h0 + 1) % sim.horas;
    const f = h - Math.floor(h);
    ids.forEach((id, k) => {
      const v = (sim.velocidades[h0].get(id) ?? 0) * (1 - f) + (sim.velocidades[h1].get(id) ?? 0) * f;
      const q = (sim.vazoes[h0].get(id) ?? 0) * (1 - f) + (sim.vazoes[h1].get(id) ?? 0) * f;
      ciclos[k] = Math.abs(v) < 0.01 ? 0 : (Math.sign(q) * Math.min(Math.abs(v) * EXAGERO_VELOCIDADE, 90)) / comprimentos[k];
    });
    const ativo = geometria.getAttribute("aAtivo") as THREE.BufferAttribute;
    const a = ativo.array as Float32Array;
    for (let i = 0; i < a.length; i++) a[i] = ciclos[trechoDoPonto[i]] === 0 ? 0 : 1;
    ativo.needsUpdate = true;
  });

  return <points name="fluxo-estimado" geometry={geometria} material={material} frustumCulled={false} />;
}
